import { isObject } from '@scalar/helpers/object/is-object';
import { objectEntries } from '@scalar/helpers/object/object-entries';
import { isSecretFieldCleared } from '@scalar/workspace-store/helpers/auth-secret-fields';
/**
 * Maps x-scalar-secret fields to their corresponding input field names.
 * This allows us to fall back to config values when auth store secrets are not available.
 */
const SECRET_TO_INPUT_FIELD_MAP = {
    'x-scalar-secret-client-id': 'x-scalar-client-id',
    'x-scalar-secret-client-secret': 'clientSecret',
    'x-scalar-secret-password': 'password',
    'x-scalar-secret-redirect-uri': 'x-scalar-redirect-uri',
    'x-scalar-secret-token': 'token',
    'x-scalar-secret-username': 'username',
    'x-scalar-secret-auth-url': 'authorizationUrl',
    'x-scalar-secret-token-url': 'tokenUrl',
};
/**
 * Keep only the defaults that would restore something. An empty default means Reset could only
 * clear the field, which the clear action already does, so the key is left off entirely.
 */
const withSecretDefaults = (defaults) => {
    const nonEmpty = Object.fromEntries(Object.entries(defaults).filter(([, value]) => typeof value === 'string' && value !== ''));
    return Object.keys(nonEmpty).length ? { 'x-scalar-secret-defaults': nonEmpty } : {};
};
/** Only string values count; anything else falls through to the next source. */
const readString = (source, key) => {
    const value = source[key];
    return typeof value === 'string' ? value : undefined;
};
const mergeFlowSecrets = (properties, configSecrets, authStoreSecrets = {}, oauth2RedirectUri, 
/**
 * OpenID Connect passes the stored flow as both the config and the auth store, so its
 * `x-scalar-secret-*` values are the user's own overrides and must not count as defaults.
 */
includeConfigSecretValues = true) => {
    const clearedFields = properties.filter((property) => isSecretFieldCleared(authStoreSecrets, property));
    const defaults = {};
    const values = Object.fromEntries(properties.map((property) => {
        // Preserve explicit clears, while legacy schema-filled empties still inherit defaults.
        const authStoreValue = readString(authStoreSecrets, property);
        const configValue = includeConfigSecretValues ? readString(configSecrets, property) : undefined;
        const configInputValue = readString(configSecrets, SECRET_TO_INPUT_FIELD_MAP[property]);
        // oauth2RedirectUri (top-level config option) is used as a global fallback for the redirect URI,
        // applied only when neither the auth store nor per-scheme config have a value. This ensures
        // the configured redirect URI persists when switching between documents with the same OAuth config,
        // because each document starts with no stored redirect URI (authStoreValue === undefined).
        const isRedirect = property === 'x-scalar-secret-redirect-uri';
        // The value and its reset target share one fallback, so Reset always restores exactly what the
        // field shows once the stored override is released.
        const fallback = isRedirect
            ? (configValue ?? configInputValue ?? oauth2RedirectUri ?? '')
            : configValue || configInputValue || '';
        // The redirect URI and explicitly cleared fields keep an empty stored value instead of falling
        // back. For the redirect URI this is the stored value ahead of the same fallback as above.
        const value = isRedirect || isSecretFieldCleared(authStoreSecrets, property)
            ? (authStoreValue ?? configValue ?? configInputValue ?? oauth2RedirectUri ?? '')
            : authStoreValue || fallback;
        defaults[property] = fallback;
        return [property, value];
    }));
    return {
        ...values,
        ...(clearedFields.length ? { 'x-scalar-secret-cleared-fields': clearedFields } : {}),
        ...withSecretDefaults(defaults),
    };
};
const storedSecret = (secrets, field) => {
    const value = secrets && Reflect.get(secrets, field);
    return typeof value === 'string' && (value || isSecretFieldCleared(secrets ?? {}, field)) ? value : undefined;
};
/** Secret extensions are not part of the strict scheme types, so they are read the same way the OAuth flows read theirs */
const documentSecret = (scheme, property) => {
    const value = property in scheme ? Reflect.get(scheme, property) : undefined;
    return typeof value === 'string' ? value : '';
};
const extractRefreshTokenSecret = (authStoreSecrets = {}) => {
    const refreshToken = authStoreSecrets['x-scalar-secret-refresh-token'];
    if (typeof refreshToken === 'string') {
        return { 'x-scalar-secret-refresh-token': refreshToken };
    }
    return {};
};
const extractCredentialsLocation = (configSecrets, authStoreSecrets = {}) => {
    const credentialsLocation = authStoreSecrets['x-scalar-credentials-location'] ??
        configSecrets['x-scalar-credentials-location'];
    return credentialsLocation ? { 'x-scalar-credentials-location': credentialsLocation } : {};
};
/**
 * Extract flow secrets and selected scopes for OAuth-like flows.
 * Reused by both oauth2 and openIdConnect security schemes.
 */
const extractOAuthFlowSecrets = (flows, storeSecrets, oauth2RedirectUri, includeConfigSecretValues = true) => {
    const selectedScopes = new Set();
    const extractedFlows = objectEntries(flows ?? {}).reduce((acc, [key, flow]) => {
        if (!isObject(flow)) {
            return acc;
        }
        // Store any selected scopes from the config
        const flowSelectedScopes = flow['selectedScopes'];
        if (Array.isArray(flowSelectedScopes)) {
            flowSelectedScopes.forEach((scope) => typeof scope === 'string' && selectedScopes.add(scope));
        }
        // Implicit flow
        if (key === 'implicit') {
            acc.implicit = {
                ...flow,
                ...mergeFlowSecrets([
                    'x-scalar-secret-client-id',
                    'x-scalar-secret-redirect-uri',
                    'x-scalar-secret-token',
                    'x-scalar-secret-auth-url',
                ], flow, storeSecrets?.implicit, oauth2RedirectUri, includeConfigSecretValues),
                ...extractRefreshTokenSecret(storeSecrets?.implicit),
            };
        }
        // Password flow
        if (key === 'password') {
            acc[key] = {
                ...flow,
                ...mergeFlowSecrets([
                    'x-scalar-secret-client-id',
                    'x-scalar-secret-client-secret',
                    'x-scalar-secret-username',
                    'x-scalar-secret-password',
                    'x-scalar-secret-token',
                    'x-scalar-secret-token-url',
                ], flow, storeSecrets?.password, undefined, includeConfigSecretValues),
                ...extractCredentialsLocation(flow, storeSecrets?.password),
                ...extractRefreshTokenSecret(storeSecrets?.password),
            };
        }
        // Client credentials flow
        if (key === 'clientCredentials') {
            acc[key] = {
                ...flow,
                ...mergeFlowSecrets([
                    'x-scalar-secret-client-id',
                    'x-scalar-secret-client-secret',
                    'x-scalar-secret-token',
                    'x-scalar-secret-token-url',
                ], flow, storeSecrets?.clientCredentials, undefined, includeConfigSecretValues),
                ...extractCredentialsLocation(flow, storeSecrets?.clientCredentials),
                ...extractRefreshTokenSecret(storeSecrets?.clientCredentials),
            };
        }
        // Device authorization flow
        if (key === 'deviceAuthorization') {
            acc[key] = {
                ...flow,
                ...mergeFlowSecrets([
                    'x-scalar-secret-client-id',
                    'x-scalar-secret-client-secret',
                    'x-scalar-secret-token',
                    'x-scalar-secret-token-url',
                ], flow, storeSecrets?.deviceAuthorization, undefined, includeConfigSecretValues),
                ...extractCredentialsLocation(flow, storeSecrets?.deviceAuthorization),
                ...extractRefreshTokenSecret(storeSecrets?.deviceAuthorization),
            };
        }
        // Authorization code flow
        if (key === 'authorizationCode') {
            acc[key] = {
                ...flow,
                ...mergeFlowSecrets([
                    'x-scalar-secret-client-id',
                    'x-scalar-secret-client-secret',
                    'x-scalar-secret-redirect-uri',
                    'x-scalar-secret-token',
                    'x-scalar-secret-auth-url',
                    'x-scalar-secret-token-url',
                ], flow, storeSecrets?.authorizationCode, oauth2RedirectUri, includeConfigSecretValues),
                ...extractCredentialsLocation(flow, storeSecrets?.authorizationCode),
                ...extractRefreshTokenSecret(storeSecrets?.authorizationCode),
            };
        }
        return acc;
    }, {});
    return { flows: extractedFlows, selectedScopes: Array.from(selectedScopes) };
};
/** Extract the secrets from the config and the auth store */
export const extractSecuritySchemeSecrets = (
// Include the config fields
scheme, authStore, name, documentSlug, oauth2RedirectUri) => {
    const secrets = authStore.getAuthSecrets(documentSlug, name);
    // Handle API Key security schemes
    if (scheme.type === 'apiKey') {
        const storeSecrets = secrets?.type === 'apiKey' ? secrets : undefined;
        const defaults = {
            'x-scalar-secret-token': documentSecret(scheme, 'x-scalar-secret-token') || scheme.value || '',
        };
        return {
            ...scheme,
            'x-scalar-secret-token': storedSecret(storeSecrets, 'x-scalar-secret-token') ?? defaults['x-scalar-secret-token'],
            ...withSecretDefaults(defaults),
        };
    }
    // Handle HTTP Auth security schemes (e.g., Basic, Bearer)
    if (scheme.type === 'http') {
        const storeSecrets = secrets?.type === 'http' ? secrets : undefined;
        const defaults = {
            'x-scalar-secret-token': documentSecret(scheme, 'x-scalar-secret-token') || scheme.token || '',
            'x-scalar-secret-username': documentSecret(scheme, 'x-scalar-secret-username') || scheme.username || '',
            'x-scalar-secret-password': documentSecret(scheme, 'x-scalar-secret-password') || scheme.password || '',
        };
        return {
            ...scheme,
            'x-scalar-secret-token': storedSecret(storeSecrets, 'x-scalar-secret-token') ?? defaults['x-scalar-secret-token'],
            'x-scalar-secret-username': storedSecret(storeSecrets, 'x-scalar-secret-username') ?? defaults['x-scalar-secret-username'],
            'x-scalar-secret-password': storedSecret(storeSecrets, 'x-scalar-secret-password') ?? defaults['x-scalar-secret-password'],
            ...withSecretDefaults(defaults),
        };
    }
    // Handle OAuth2 security schemes and all supported flows
    if (scheme.type === 'oauth2') {
        const storeSecrets = secrets?.type === 'oauth2' ? secrets : undefined;
        const extracted = extractOAuthFlowSecrets(scheme.flows, storeSecrets, oauth2RedirectUri, true);
        const configuredDefaultScopes = Array.isArray(scheme['x-default-scopes'])
            ? scheme['x-default-scopes'].filter((scope) => typeof scope === 'string')
            : [];
        const mergedDefaultScopes = Array.from(new Set([...configuredDefaultScopes, ...extracted.selectedScopes]));
        return {
            ...scheme,
            flows: extracted.flows,
            'x-default-scopes': mergedDefaultScopes,
        };
    }
    // OpenID Connect uses auth-store-only discovered flows, but we expose them in OAuth-like flow format.
    if (scheme.type === 'openIdConnect') {
        const storeSecrets = secrets?.type === 'openIdConnect' ? secrets : undefined;
        const extracted = extractOAuthFlowSecrets({
            implicit: storeSecrets?.implicit,
            password: storeSecrets?.password,
            clientCredentials: storeSecrets?.clientCredentials,
            authorizationCode: storeSecrets?.authorizationCode,
            deviceAuthorization: storeSecrets?.deviceAuthorization,
        }, storeSecrets, oauth2RedirectUri, 
        // The flows above are the stored flows themselves, so only discovered or input keys are defaults
        false);
        return {
            ...scheme,
            ...(objectEntries(extracted.flows).length ? { flows: extracted.flows } : {}),
        };
    }
    // SASL-style schemes (userPassword, plain, scramSha256, scramSha512): username + password,
    // with the same config fallbacks as HTTP basic.
    if (scheme.type === 'userPassword' ||
        scheme.type === 'plain' ||
        scheme.type === 'scramSha256' ||
        scheme.type === 'scramSha512') {
        const storeSecrets = secrets?.type === scheme.type ? secrets : undefined;
        return {
            ...scheme,
            type: scheme.type,
            'x-scalar-secret-username': storeSecrets?.['x-scalar-secret-username'] || scheme.username || '',
            'x-scalar-secret-password': storeSecrets?.['x-scalar-secret-password'] || scheme.password || '',
        };
    }
    // X509: a client certificate + private key pair (PEM), stored in the auth store only.
    if (scheme.type === 'X509') {
        const storeSecrets = secrets?.type === 'X509' ? secrets : undefined;
        return {
            ...scheme,
            type: scheme.type,
            'x-scalar-secret-client-certificate': storeSecrets?.['x-scalar-secret-client-certificate'] || '',
            'x-scalar-secret-private-key': storeSecrets?.['x-scalar-secret-private-key'] || '',
        };
    }
    // Encryption schemes (symmetricEncryption, asymmetricEncryption): a single key value in the token slot.
    if (scheme.type === 'symmetricEncryption' || scheme.type === 'asymmetricEncryption') {
        const storeSecrets = secrets?.type === scheme.type ? secrets : undefined;
        return {
            ...scheme,
            type: scheme.type,
            'x-scalar-secret-token': storeSecrets?.['x-scalar-secret-token'] || scheme.token || '',
        };
    }
    // GSSAPI (Kerberos): the service name the client authenticates against.
    if (scheme.type === 'gssapi') {
        const storeSecrets = secrets?.type === 'gssapi' ? secrets : undefined;
        return {
            ...scheme,
            type: scheme.type,
            'x-scalar-secret-service-name': storeSecrets?.['x-scalar-secret-service-name'] || '',
        };
    }
    return scheme;
};
